import { useEffect, useState } from "react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import LiveTicker from "@/components/LiveTicker";
import { api, buildConnectUrl, connectToDeriv, AFFILIATE_URL, TELEGRAM_URL, DERIV_CASHIER_URL, tradeAuth } from "@/lib/api";
import { LobbyTiles } from "@/components/casino/LobbyTiles";
import { AutopilotPanel } from "@/components/casino/AutopilotPanel";
import { PlayerRewards } from "@/components/casino/PlayerRewards";
import { LimitsPanel } from "@/components/casino/LimitsPanel";
import { ReferralCard } from "@/components/casino/ReferralCard";
import { UsernameCard } from "@/components/casino/UsernameCard";
import { Avatar } from "@/components/casino/Avatar";
import { LuckyHourBanner } from "@/components/casino/LuckyHourBanner";
import { DemoRealToggle } from "@/components/casino/DemoRealToggle";
import { LOBBY_ART } from "@/lib/casinoArt";
import { fetchAccountSummary, summarise, normaliseAccount, persistSummary, subscribeBalance } from "@/lib/derivWs";
import { ResponsiveContainer, XAxis, YAxis, Tooltip, Area, AreaChart } from "recharts";
import { TrendingUp, TrendingDown, Wallet, Flame, Bot as BotIcon, Dices, LineChart as LineIcon, ArrowUpRight, Sparkles, LogIn, X, ShieldCheck, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

export default function Dashboard() {
  const [balance, setBalance] = useState({ balance: null, currency: "USD", loginid: null });
  const [ticks, setTicks] = useState([]);
  const [trending, setTrending] = useState([]);
  const [symbol, setSymbol] = useState("R_100");
  // All linked Deriv accounts the user owns (demo + real) — populated from the browser WS authorize.
  const [accountList, setAccountList] = useState([]);
  const [allBalances, setAllBalances] = useState({}); // keyed by loginid: {balance, currency}
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const sessionId = typeof window !== "undefined" ? localStorage.getItem("naevo_session") : null;
  // Account identity cached from AuthCallback — always shown, even when the balance read fails
  const savedLoginid = typeof window !== "undefined" ? (localStorage.getItem("naevo_loginid") || "") : "";
  const savedCurrency = typeof window !== "undefined" ? (localStorage.getItem("naevo_currency") || "USD") : "USD";
  const storedAccounts = (() => { try { return JSON.parse(localStorage.getItem("naevo_accounts") || "[]").filter((a) => a.account_type); } catch { return []; } })();
  const savedAccountType = typeof window !== "undefined" ? (localStorage.getItem("naevo_account_type") || "") : ""; // "real" | "demo"
  const savedToken = typeof window !== "undefined" ? (localStorage.getItem("naevo_token") || "") : "";
  // Treat a literal "UNKNOWN" loginid (left over from an old failed authorize probe) as unverified.
  const isUnverified = savedLoginid === "UNKNOWN" || !savedToken;
  const isConnected = Boolean(sessionId && savedLoginid);
  const isDemo = savedAccountType === "demo";
  // Auto-open the Connect modal when the user lands on /dashboard without a session
  const [showConnect, setShowConnect] = useState(!isConnected);

  useEffect(() => {
    // Live balance + account list from the user's browser WS (bypasses pod WS block).
    // IMPORTANT: We ALWAYS try the browser WS FIRST with the stored token, even when the
    // cached loginid is "UNKNOWN" or missing — because a stale UNKNOWN is almost always a
    // leftover from the pre-fix era (when the WS was called with an alphanumeric app_id and
    // authorize failed silently). With the numeric WS app_id now in place the same token
    // typically works fine. Only if the WS *and* the refresh_token flow both fail do we
    // fall back to a full re-OAuth.
    let cancelled = false;
    let unsubBalance = null;
    const apply = (summary, token) => {
      if (cancelled || !summary.loginid) return;
      persistSummary(summary, token);
      setBalance(summary.balance);
      setAccountList(summary.accounts);
      setAllBalances(summary.all_balances);
      sessionStorage.removeItem("naevo_auto_reconnect_tried");
      if (!unsubBalance) {
        unsubBalance = subscribeBalance(token, summary.loginid, (b) => {
          if (!cancelled && b.loginid === localStorage.getItem("naevo_active_account")) {
            setBalance({ balance: b.balance, currency: b.currency, loginid: b.loginid });
            localStorage.setItem("naevo_balance", Number(b.balance).toFixed(2));
          }
        });
      }
    };
    async function pullLive() {
      // Read fresh each tick — a refresh may have rotated the token.
      const token = localStorage.getItem("naevo_token") || "";
      const sid = localStorage.getItem("naevo_session") || "";
      const preferred = localStorage.getItem("naevo_active_account") || "";
      if (!token && !sid) return "no-token";
      // 1. Browser → Deriv REST (new API)
      if (token) {
        try {
          apply(await fetchAccountSummary(token, preferred), token);
          return "ok";
        } catch (e) {
          if (e?.message === "InvalidToken" && sid) {
            try {
              const r = await api.post("/auth/refresh", { session_id: sid });
              if (r.data?.token) {
                apply(await fetchAccountSummary(r.data.token, preferred), r.data.token);
                return "ok";
              }
            } catch {}
          }
        }
      }
      // 2. Backend fallback (also refreshes an expired token server-side)
      if (!sid) return "token-dead";
      try {
        const r = await api.get("/account/accounts", { params: { session_id: sid } });
        const tok = r.data.token || token;
        const accounts = (r.data.accounts || []).map((a) => normaliseAccount(a, tok));
        if (!accounts.length) return "ws-fail";
        apply(summarise(accounts, preferred || r.data.active_account_id), tok);
        return "ok";
      } catch (e) {
        return e?.response?.status === 401 || e?.response?.status === 404 ? "token-dead" : "ws-fail";
      }
    }
    // Kick the live pull. If the result is "no-token" or "token-dead" AND we think we
    // were connected, auto-start a fresh OAuth (one-shot guard via sessionStorage).
    (async () => {
      const result = await pullLive();
      if (cancelled) return;
      if ((result === "no-token" || result === "token-dead") && isConnected) {
        if (!sessionStorage.getItem("naevo_auto_reconnect_tried")) {
          sessionStorage.setItem("naevo_auto_reconnect_tried", "1");
          toast.info("Refreshing your Deriv session…");
          const m = await import("@/lib/api");
          m.connectToDeriv();
        }
      }
    })();
    const t = setInterval(pullLive, 15000);
    api.get("/bot/trending").then((r) => {
      setTrending(r.data);
      if (r.data[0]) try { localStorage.setItem("naevo_top_pair", `${r.data[0].symbol} (score ${r.data[0].score})`); } catch {}
    }).catch(() => {});
    const r = setInterval(() => api.get("/bot/trending").then((r) => {
      setTrending(r.data);
      if (r.data[0]) try { localStorage.setItem("naevo_top_pair", `${r.data[0].symbol} (score ${r.data[0].score})`); } catch {}
    }).catch(() => {}), 4000);
    return () => { cancelled = true; clearInterval(r); clearInterval(t); if (unsubBalance) unsubBalance(); };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const load = () => api.get("/markets/ticks", { params: { symbol, count: 60 } }).then((r) => {
      const prices = r.data.prices || [];
      setTicks(prices.map((p, i) => ({ idx: i, price: p })));
    }).catch(() => setTicks([]));
    load();
    const id = setInterval(load, 6000);
    return () => clearInterval(id);
  }, [symbol]);

  const last = ticks[ticks.length - 1]?.price;
  const first = ticks[0]?.price;
  const change = last && first ? ((last - first) / first) * 100 : 0;
  const topPair = trending[0];

  return (
    <div>
      <SiteNav />

      {/* Connect-your-Deriv-account modal — pops when user hits dashboard without a session */}
      <AnimatePresence>
        {showConnect && !isConnected && (
          <motion.div
            key="connect-modal"
            className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="connect-modal"
          >
            <motion.div
              className="relative w-full max-w-md rounded-3xl border border-white/10 bg-[#0C0714] p-7 shadow-2xl"
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 24 }}
            >
              <button
                onClick={() => setShowConnect(false)}
                className="absolute top-4 right-4 h-9 w-9 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:bg-white/5"
                data-testid="connect-modal-close"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="flex items-center gap-3 text-magenta">
                <LogIn className="h-6 w-6" />
                <div className="font-display text-2xl text-white">Connect your Deriv account</div>
              </div>
              <p className="text-white/60 text-sm mt-3 leading-relaxed">
                Sign in with your normal Deriv email and password — no API tokens, no copy-paste. We're a registered Deriv partner; your trades stay on your Deriv account.
              </p>
              <a
                href={buildConnectUrl()}
                onClick={(e) => { e.preventDefault(); connectToDeriv(); }}
                className="btn-magenta mt-5 rounded-2xl w-full h-14 flex items-center justify-center gap-3 font-medium text-base"
                data-testid="connect-modal-login"
              >
                <LogIn className="h-5 w-5" /> Login with Deriv
              </a>
              <div className="mt-3 flex items-center gap-2 text-xs text-[color:var(--naevo-success)]">
                <ShieldCheck className="h-3.5 w-3.5" />
                <span>Official Deriv OAuth · markup attribution enabled · 30-second setup</span>
              </div>
              <a
                href={AFFILIATE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 block rounded-2xl border border-lavender/50 bg-lavender/5 px-5 py-4 text-center hover:bg-lavender/10 transition"
                data-testid="connect-modal-signup"
              >
                <div className="font-medium text-white">New to Deriv? Create a free account →</div>
                <div className="text-xs text-white/50 mt-1">Takes 2 minutes — then come back and tap Login with Deriv</div>
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-6 sm:py-10">
        <LiveTicker />
        {/* Casino lobby — cashier bar */}
        <section className="casino-frame rounded-3xl relative overflow-hidden mt-2" data-testid="lobby-cashier">
          <img src={LOBBY_ART.hero} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/30" />
          <div className="bulb-row absolute top-0 left-0 right-0" />
          <div className="relative p-5 sm:p-8 flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div>
              <div className="text-[10px] sm:text-xs uppercase tracking-[0.35em] text-gold/80 flex items-center gap-2">
                <Sparkles className="h-3 w-3" /> Naevo Casino · Lobby
              </div>
              <h1 className="font-casino text-gold-grad text-4xl sm:text-5xl lg:text-6xl mt-2">Welcome to the floor</h1>
              {isConnected ? (
                <p className="mt-2 text-sm font-mono-naevo text-white/70" data-testid="hero-connected-note">
                  {isUnverified ? "Loading your Deriv account…" : <>Playing on <span className={isDemo ? "text-[#2AABEE]" : "text-gold"}>{isDemo ? "DEMO" : "REAL"}</span> · {savedLoginid}</>}
                </p>
              ) : (
                <p className="mt-2 text-sm text-white/70">Connect your Deriv account to load your balance and play live.</p>
              )}
              {!isConnected && (
                <div className="mt-5 flex gap-3 flex-wrap">
                  <a href={buildConnectUrl()} onClick={(e) => { e.preventDefault(); connectToDeriv(); }} className="btn-gold rounded-full px-6 py-2.5 text-sm flex items-center gap-2" data-testid="dashboard-connect-deriv">Login with Deriv</a>
                  <a href={AFFILIATE_URL} target="_blank" rel="noreferrer" className="btn-ghost rounded-full px-6 py-2.5 text-sm flex items-center gap-2" data-testid="dashboard-signup-cta">Sign up <ArrowUpRight className="h-4 w-4" /></a>
                </div>
              )}
            </div>
            <div className="lg:text-right" data-testid="balance-card">
              <div className="text-[10px] uppercase tracking-[0.35em] text-gold/70">Chip balance</div>
              <div className="font-mono-naevo text-4xl sm:text-5xl text-white mt-1" data-testid="balance-amount">
                {balance.balance !== null ? Number(balance.balance).toFixed(2) : (isConnected ? "···" : "—")}
                <span className="text-sm text-gold/70 ml-2">{balance.currency || savedCurrency}</span>
              </div>
              {isConnected && !isUnverified && (
                <div className="mt-3 flex flex-col sm:flex-row lg:justify-end gap-3 items-stretch sm:items-start">
                  <DemoRealToggle sessionId={sessionId} accounts={accountList.length ? accountList : storedAccounts} activeId={balance.loginid || savedLoginid} />
                  <div className="flex gap-2">
                    {["deposit", "withdraw"].map((action) => (
                      <button
                        key={action}
                        onClick={() => window.open(`${AFFILIATE_URL}&redirect=${encodeURIComponent(DERIV_CASHIER_URL)}`, "_blank", "noopener,noreferrer")}
                        data-testid={action === "deposit" ? "deposit-btn" : "withdraw-btn"}
                        className={`${action === "deposit" ? "btn-velvet" : "btn-ghost"} rounded-full px-5 py-2.5 text-sm whitespace-nowrap`}
                      >
                        {action === "deposit" ? "Deposit on Deriv" : "Withdraw on Deriv"}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {isConnected && isUnverified && (
                <a href={buildConnectUrl()} onClick={(e) => { e.preventDefault(); connectToDeriv(); }} className="btn-gold rounded-full px-6 py-2.5 text-sm mt-3 inline-flex" data-testid="dashboard-reconnect-token">Reconnect to load balance</a>
              )}
            </div>
          </div>
        </section>

        {/* Lobby tiles */}
        <div className="mt-6"><LobbyTiles /></div>


        {/* Autopilot toggle — always-on background bot */}
        <div className="mt-6"><LuckyHourBanner /></div>
        {isConnected && <AutopilotPanel sessionId={sessionId} />}
        {isConnected && (
          <div className="mt-6 grid grid-cols-1 gap-6">
            <UsernameCard sessionId={sessionId} />
            <PlayerRewards />
            <LimitsPanel />
            <ReferralCard sessionId={sessionId} />
          </div>
        )}

        {/* How-to-play info cards */}
        <HowToPlay />

        {/* Live Winners Leaderboard */}
        <LiveWinners />

        <a href={TELEGRAM_URL} target="_blank" rel="noreferrer" data-testid="jump-to-telegram" className="mt-6 flex items-center justify-between rounded-2xl border border-[#2AABEE]/40 bg-[#2AABEE]/5 hover:bg-[#2AABEE]/10 p-5 transition-colors">
          <div>
            <div className="font-casino text-lg text-[#2AABEE]">Signals channel</div>
            <div className="text-xs text-white/50 mt-1">Live setups + wins · join on Telegram</div>
          </div>
          <ArrowUpRight className="h-5 w-5 text-[#2AABEE]" />
        </a>
      </main>
      <SiteFooter />
    </div>
  );
}


function LiveWinners() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    const pull = () => api.get("/arcade/live-winners", { params: { limit: 12 } })
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
    pull();
    const id = setInterval(pull, 15000);
    return () => clearInterval(id);
  }, []);
  const gameLabel = { penalty: "Penalty", flip: "Red/Black", hoop: "Hoop", treasure: "Treasure" };
  return (
    <div className="mt-6 casino-frame rounded-2xl p-5" data-testid="live-winners">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-white/40">Live winners</div>
          <div className="font-display text-2xl mt-1 flex items-center gap-2">
            Who just won <span className="text-xs text-white/50 font-sans">· real plays, live from the arcade</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-[color:var(--naevo-success)] animate-pulse" />
          <span className="text-xs font-mono-naevo text-[color:var(--naevo-success)]">LIVE</span>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mt-4">
        {rows.map((r, i) => (
          <motion.div
            key={`${r.handle}-${i}`}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.04 }}
            data-testid={`winner-${i}`}
            className={`rounded-xl border px-3 py-2.5 flex items-center justify-between gap-2 ${r.live ? "border-[color:var(--naevo-success)]/40 bg-[color:var(--naevo-success)]/5" : "border-white/10 bg-white/[0.02]"}`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <Avatar id={r.avatar} size={30} />
              <div className="min-w-0">
                <div className="text-sm truncate font-mono-naevo">{r.handle}</div>
                <div className="text-[10px] text-white/40">{gameLabel[r.game] || r.game}{r.account_type === "demo" ? " · demo" : ""}</div>
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="font-mono-naevo text-sm text-[color:var(--naevo-success)]">+${r.profit.toFixed(2)}</div>
              <div className="text-[10px] text-white/40">on ${r.stake.toFixed(2)}</div>
            </div>
          </motion.div>
        ))}
        {rows.length === 0 && <div className="col-span-3 text-white/40 text-sm text-center py-6" data-testid="live-winners-empty">No wins yet today — be the first on the board.</div>}
      </div>
    </div>
  );
}

function HowToPlay() {
  const [signal, setSignal] = useState(null);
  useEffect(() => {
    const pull = () => api.get("/arcade/signal").then((r) => setSignal(r.data)).catch(() => {});
    pull();
    const id = setInterval(pull, 15000);
    return () => clearInterval(id);
  }, []);
  const digits = signal?.locked?.last_digits || [];
  return (
    <div className="mt-6 rounded-2xl border border-white/10 bg-[#0C0914] p-5" data-testid="how-to-play">
      <div className="text-xs uppercase tracking-widest text-white/40">How it works</div>
      <div className="font-display text-2xl mt-1">Three taps. One signal. Real contracts.</div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="htp-step1">
          <div className="text-magenta text-xs font-mono-naevo">STEP 1</div>
          <div className="font-display text-lg mt-1">Open any arcade game</div>
          <div className="text-xs text-white/50 mt-1">Penalty · Flip · Hoop · Treasure · Dice · Wheel — every one maps to EVEN / ODD.</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="htp-step2">
          <div className="text-magenta text-xs font-mono-naevo">STEP 2</div>
          <div className="font-display text-lg mt-1">Signal engine locks the strongest market</div>
          <div className="text-xs text-white/50 mt-1">10 Deriv markets · last 50 ticks · auto-picks the most skewed EVEN/ODD setup.</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="htp-step3">
          <div className="text-magenta text-xs font-mono-naevo">STEP 3</div>
          <div className="font-display text-lg mt-1">Tap · win 95% · auto-recover losses</div>
          <div className="text-xs text-white/50 mt-1">$1 stake → +$0.89 on a win. Smart Recovery sizes the next stake so one win wins back every loss + 5%, then returns to base.</div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded-xl border border-white/10 bg-black/40 p-4" data-testid="htp-live-ticks">
          <div className="flex items-center justify-between text-xs font-mono-naevo">
            <span className="text-[color:var(--naevo-success)] uppercase tracking-widest">Live ticks</span>
            <span className="text-white/40">{signal?.locked?.label || "scanning…"}</span>
          </div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {digits.length === 0 && <span className="text-white/40 text-sm">Loading…</span>}
            {digits.map((d, i) => {
              const isLast = i === digits.length - 1;
              const isEven = d % 2 === 0;
              return (
                <span key={i} className={`w-7 h-7 flex items-center justify-center rounded font-mono-naevo text-sm border ${
                  isLast ? "border-[#F5A623] bg-[#F5A623]/10 text-[#F5A623]" :
                  isEven ? "border-[color:var(--naevo-success)]/50 bg-[color:var(--naevo-success)]/10 text-[color:var(--naevo-success)]" :
                           "border-[color:var(--naevo-danger)]/50 bg-[color:var(--naevo-danger)]/10 text-[color:var(--naevo-danger)]"
                }`}>{d}</span>
              );
            })}
          </div>
          <div className="text-[10px] text-white/40 mt-2 font-mono-naevo">Green = EVEN · Red = ODD · Orange = latest</div>
        </div>

        <div className="rounded-xl border border-white/10 bg-black/40 p-4" data-testid="htp-stake-table">
          <div className="text-xs font-mono-naevo uppercase tracking-widest text-cyan-300">Stake examples</div>
          <div className="mt-3 space-y-1.5 text-sm font-mono-naevo">
            {[
              { s: 1,   w: 0.89,   r: 1.19 },
              { s: 5,   w: 4.43,   r: 5.93 },
              { s: 10,  w: 8.86,   r: 11.86 },
              { s: 25,  w: 22.15,  r: 29.63 },
            ].map((row) => (
              <div key={row.s} className="flex items-center justify-between">
                <span className="text-white/60">${row.s.toFixed(2)}</span>
                <span className="text-[color:var(--naevo-success)]">+${row.w.toFixed(2)} on win</span>
                <span className="text-[#F5A623]">recovery stake ${row.r.toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="text-[10px] text-white/40 mt-2">Risk per round · payout math at a glance.</div>
        </div>
      </div>
    </div>
  );
}


/**
 * Reads every linked account from localStorage (populated on each OAuth) and shows a dropdown
 * with instant switch + "Add account" button to trigger another OAuth for a different Deriv account.
 */
